import { useState } from 'react'
import { tonleiterMidi, midiZuNotenName } from '../domain/tonarten'
import { spieleTon } from '../domain/audio'
import type { Tonart } from '../types'

const START_MIDI = 48   // C3
const END_MIDI   = 72   // C5
const ANZAHL_WEISS = 15

const SCHWARZE_OFFSETS_IM_OKTAV: Record<number, number> = {
  1: 0, 3: 1, 6: 3, 8: 4, 10: 5,
}

function istSchwarz(midi: number): boolean {
  const pc = midi % 12
  return pc === 1 || pc === 3 || pc === 6 || pc === 8 || pc === 10
}

function weisseTasten(): number[] {
  const tasten: number[] = []
  for (let m = START_MIDI; m <= END_MIDI; m++) {
    if (!istSchwarz(m)) tasten.push(m)
  }
  return tasten
}

function schwarzeTasten(): number[] {
  const tasten: number[] = []
  for (let m = START_MIDI; m <= END_MIDI; m++) {
    if (istSchwarz(m)) tasten.push(m)
  }
  return tasten
}

interface Props {
  hervorhebung?: Tonart | null
  onKlick?: (midi: number) => void
}

const TASTEN_WEISS_BREITE = 40
const TASTEN_WEISS_HOEHE  = 160
const TASTEN_SCHWARZ_HOEHE = 100

export function Klaviatur({ hervorhebung, onKlick }: Props) {
  const [aktiv, setAktiv] = useState<Set<number>>(new Set())
  const weiss = weisseTasten()
  const schwarz = schwarzeTasten()
  const tonartPcs = hervorhebung
    ? new Set(tonleiterMidi(hervorhebung).map(m => m % 12))
    : new Set<number>()

  const breite = ANZAHL_WEISS * TASTEN_WEISS_BREITE

  function klicke(midi: number) {
    spieleTon(midi, {
      onStart: m => setAktiv(prev => new Set(prev).add(m)),
      onEnd:   m => setAktiv(prev => { const n = new Set(prev); n.delete(m); return n }),
    })
    onKlick?.(midi)
  }

  function weisserIndex(midi: number): number {
    return weiss.indexOf(midi)
  }

  function schwarzePosition(midi: number): number {
    const oktavStart = midi - (midi % 12)
    const offset = SCHWARZE_OFFSETS_IM_OKTAV[midi % 12]
    const oktavOffset = ((oktavStart - 48) / 12) * 7
    return (oktavOffset + offset + 1) * TASTEN_WEISS_BREITE - TASTEN_WEISS_BREITE * 0.3
  }

  return (
    <svg viewBox={`0 0 ${breite} ${TASTEN_WEISS_HOEHE}`} className="klaviatur" role="img" aria-label="Klaviatur C3 bis C5">
      {weiss.map((midi) => {
        const x = weisserIndex(midi) * TASTEN_WEISS_BREITE
        const inTonart = tonartPcs.has(midi % 12)
        const istTonika = hervorhebung && midi % 12 === hervorhebung.tonicMidi % 12
        const istGedrueckt = aktiv.has(midi)
        const fill = istGedrueckt ? '#d4a23c'
                  : istTonika    ? '#e8b95c'
                  : inTonart     ? '#fef0c7'
                  : '#ffffff'
        return (
          <g key={midi} onClick={() => klicke(midi)} style={{ cursor: 'pointer' }}>
            <rect
              x={x} y={0}
              width={TASTEN_WEISS_BREITE - 1} height={TASTEN_WEISS_HOEHE}
              fill={fill} stroke="#3a3a3a" strokeWidth={1}
            />
            <text x={x + TASTEN_WEISS_BREITE / 2} y={TASTEN_WEISS_HOEHE - 8}
                  textAnchor="middle" fontSize={10} fill="#5a5a5a" pointerEvents="none">
              {midiZuNotenName(midi)}
            </text>
          </g>
        )
      })}
      {schwarz.map((midi) => {
        const x = schwarzePosition(midi)
        const inTonart = tonartPcs.has(midi % 12)
        const istGedrueckt = aktiv.has(midi)
        const fill = istGedrueckt ? '#d4a23c'
                  : inTonart     ? '#7a5a2c'
                  : '#1a1a1a'
        return (
          <rect
            key={midi}
            x={x} y={0}
            width={TASTEN_WEISS_BREITE * 0.6} height={TASTEN_SCHWARZ_HOEHE}
            fill={fill} stroke="#000" strokeWidth={1}
            onClick={() => klicke(midi)}
            style={{ cursor: 'pointer' }}
          />
        )
      })}
    </svg>
  )
}
